import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, RefreshCw, Loader2, Lock, ArrowLeft, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface LogoApprovalStepProps {
  rawLogoUrl: string;
  brandName: string;
  attemptNumber: number;
  maxAttempts: number;
  isProcessing: boolean;
  onApprove: () => void;
  onRegenerate: () => void;
  onBack?: () => void;
}

export const LogoApprovalStep = ({
  rawLogoUrl,
  brandName,
  attemptNumber,
  maxAttempts,
  isProcessing,
  onApprove,
  onRegenerate,
  onBack,
}: LogoApprovalStepProps) => {
  return (
    <div className="animate-fade-in">
      {/* Header - Unified with wizard design system */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-3">
          <Sparkles className="w-4 h-4 text-white/90" />
          <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
            Logo Gerada
          </span>
        </div>
        <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white mb-2 sm:mb-3 text-balance leading-snug tracking-tight break-words">
          Sua logo está pronta!
        </h2>
        <p className="text-white/80 text-sm sm:text-base lg:text-lg text-balance leading-relaxed">
          Aprove para criar a identidade visual completa ou gere uma nova opção.
        </p>
      </div>

      {/* Logo Preview Card - Unified styling */}
      <Card className="max-w-md mx-auto overflow-hidden bg-white/95 backdrop-blur shadow-2xl shadow-black/10 border-0 rounded-2xl">
        <div className="p-6 sm:p-8">
          <div className="relative aspect-square w-full rounded-xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
            <img
              src={rawLogoUrl}
              alt={`Logo ${brandName}`}
              className="w-full h-full object-contain p-4"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
            {/* Watermark overlay - Responsivo e denso */}
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <div 
                className="absolute inset-0"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='40'%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='rgba(0,0,0,0.35)' font-size='9' font-family='system-ui, sans-serif' font-weight='700' transform='rotate(-25 50 20)'%3EPREVIEW%3C/text%3E%3C/svg%3E")`,
                  backgroundRepeat: 'repeat',
                  backgroundSize: '80px 32px'
                }}
              />
            </div>
          </div>

          {/* Attempt Counter */}
          <div className="mt-4 flex justify-center">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
              Tentativa {attemptNumber}/{maxAttempts}
            </span>
          </div>
        </div>
      </Card>

      {/* Action Buttons - Mobile-optimized touch targets */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-8 max-w-md mx-auto">
        {onBack && (
          <Button
            onClick={onBack}
            disabled={isProcessing}
            variant="ghost"
            className="w-full sm:w-auto text-white hover:bg-white/20 min-h-[48px] px-6 rounded-xl order-3 sm:order-1"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        )}
        
        <Button
          onClick={onRegenerate}
          disabled={isProcessing || attemptNumber >= maxAttempts}
          variant="outline"
          className="w-full sm:w-auto border-white/40 bg-white/10 text-white hover:bg-white/20 min-h-[52px] px-6 rounded-xl font-medium order-2"
        >
          <RefreshCw className={cn("w-5 h-5 mr-2", isProcessing && "animate-spin")} />
          🔄 Gerar Nova Opção
          {attemptNumber < maxAttempts && (
            <span className="ml-2 text-xs bg-white/15 px-2 py-0.5 rounded-full">
              {maxAttempts - attemptNumber}
            </span>
          )}
        </Button>

        <Button
          onClick={onApprove}
          disabled={isProcessing}
          className="w-full sm:w-auto bg-white text-primary hover:bg-white/95 min-h-[56px] px-8 rounded-xl font-bold text-base shadow-lg shadow-black/20 order-1 sm:order-3"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Criando...
            </>
          ) : (
            <>
              <Check className="w-5 h-5 mr-2" />
              Aprovar e Continuar
              <Lock className="w-4 h-4 ml-2 opacity-50" />
            </>
          )}
        </Button>
      </div>

      {attemptNumber >= maxAttempts && (
        <p className="text-center text-sm text-white/60 mt-4">
          Você atingiu o limite de tentativas gratuitas.
        </p>
      )}

      {/* Info Box - Card claro com alto contraste */}
      <Card className="max-w-md mx-auto p-5 bg-white/95 backdrop-blur border-0 shadow-lg mt-8 rounded-xl">
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Check className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h4 className="font-bold text-base text-gray-900 mb-2">
              O que você receberá:
            </h4>
            <ul className="text-sm text-gray-700 space-y-1.5 font-medium">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                Logo em PNG transparente
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                Paleta de 4 cores profissional
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                3 fontes tipográficas gratuitas
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                Tagline e bio para redes sociais
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};
